import { z } from "zod";
// src/schema.ts
const rgbChannelsSchema = z.string().regex(/^\d{1,3} \d{1,3} \d{1,3}$/, "Expected \"R G B\" channels, e.g. \"31 143 255\"").refine((v) => v.split(" ").every((n) => Number(n) <= 255), { message: "Each channel must be 0–255" });
const cssColor = z.string().trim().min(1, "Expected a non-empty CSS colour");
const sidebarColorsSchema = z.object({
	sidebar: rgbChannelsSchema,
	sidebarForeground: rgbChannelsSchema,
	sidebarPrimary: rgbChannelsSchema,
	sidebarPrimaryForeground: rgbChannelsSchema,
	sidebarAccent: rgbChannelsSchema,
	sidebarAccentForeground: rgbChannelsSchema,
	sidebarBorder: rgbChannelsSchema,
	sidebarRing: rgbChannelsSchema,
	sidebarHover: cssColor.optional(),
	sidebarHoverForeground: cssColor.optional(),
	sidebarSelected: cssColor.optional(),
	sidebarSelectedForeground: cssColor.optional()
});
const chartColorsSchema = z.object({
	chart1: rgbChannelsSchema,
	chart2: rgbChannelsSchema,
	chart3: rgbChannelsSchema,
	chart4: rgbChannelsSchema,
	chart5: rgbChannelsSchema
});
const colorsSchema = z.object({
	foreground: rgbChannelsSchema,
	background: rgbChannelsSchema,
	muted: rgbChannelsSchema,
	mutedForeground: rgbChannelsSchema,
	border: rgbChannelsSchema,
	primary: rgbChannelsSchema,
	primaryForeground: rgbChannelsSchema,
	card: rgbChannelsSchema.optional(),
	cardForeground: rgbChannelsSchema.optional(),
	popover: rgbChannelsSchema.optional(),
	popoverForeground: rgbChannelsSchema.optional(),
	secondary: rgbChannelsSchema.optional(),
	secondaryForeground: rgbChannelsSchema.optional(),
	accent: rgbChannelsSchema.optional(),
	accentForeground: rgbChannelsSchema.optional(),
	input: rgbChannelsSchema.optional(),
	inputBackground: rgbChannelsSchema.optional(),
	ring: rgbChannelsSchema.optional(),
	ringOffset: rgbChannelsSchema.optional(),
	success: rgbChannelsSchema.optional(),
	successForeground: rgbChannelsSchema.optional(),
	warning: rgbChannelsSchema.optional(),
	warningForeground: rgbChannelsSchema.optional(),
	info: rgbChannelsSchema.optional(),
	infoForeground: rgbChannelsSchema.optional(),
	destructive: rgbChannelsSchema,
	destructiveForeground: rgbChannelsSchema,
	hover: cssColor.optional(),
	hoverForeground: cssColor.optional(),
	pressed: cssColor.optional(),
	selected: cssColor.optional(),
	selectedForeground: cssColor.optional(),
	scrim: cssColor.optional(),
	chart: chartColorsSchema.optional(),
	sidebar: sidebarColorsSchema.optional()
}).partial();
const fontWeightValue = z.string().regex(/^\d{1,4}$/, "Expected a numeric font-weight, e.g. \"500\"").refine((v) => Number(v) >= 1 && Number(v) <= 1e3, { message: "font-weight must be between 1 and 1000" });
const fontWeightScaleSchema = z.object({
	normal: fontWeightValue.optional(),
	medium: fontWeightValue.optional(),
	semibold: fontWeightValue.optional(),
	bold: fontWeightValue.optional()
});
const letterSpacingSchema = z.object({ chrome: z.string().trim().min(1).optional() });
const cssValue = z.string().trim().min(1, "Expected a non-empty CSS value");
const typeStepSchema = z.object({
	size: cssValue,
	weight: cssValue,
	leading: cssValue,
	tracking: cssValue
}).partial();
const typeScaleSchema = z.object({
	display: typeStepSchema,
	heading: typeStepSchema,
	body: typeStepSchema
}).partial();
const typographySchema = z.object({
	fontFamily: z.string().trim().min(1).optional(),
	fontDisplay: z.string().trim().min(1).optional(),
	fontMono: z.string().trim().min(1).optional(),
	weight: fontWeightScaleSchema.optional(),
	letterSpacing: letterSpacingSchema.optional(),
	typeScale: typeScaleSchema.optional()
});
const radiiSchema = z.object({
	sm: z.string(),
	md: z.string(),
	lg: z.string(),
	card: cssValue.optional(),
	popover: cssValue.optional(),
	modal: cssValue.optional(),
	sheet: cssValue.optional(),
	toast: cssValue.optional()
});
const opacitySchema = z.object({ disabled: z.string().regex(/^(0(\.\d+)?|1(\.0+)?|\.\d+)$/, "Expected an opacity from 0 to 1") }).partial();
const shadowsSchema = z.object({
	sm: cssValue,
	md: cssValue,
	lg: cssValue,
	lift: cssValue
}).partial();
const spacingSchema = z.object({
	gutter: cssValue,
	inset: cssValue
}).partial();
const motionSchema = z.object({
	duration: cssValue,
	easing: cssValue
}).partial();
const gradientSchema = z.object({ brand: cssValue }).partial();
const logoSource = z.string().trim().min(1);
const brandLogo = z.object({
	mark: logoSource.optional(),
	wordmark: logoSource.optional(),
	markDark: logoSource.optional(),
	wordmarkDark: logoSource.optional()
});
const themeSchema = z.object({
	colors: colorsSchema.optional(),
	colorsDark: colorsSchema.optional(),
	typography: typographySchema.optional(),
	radii: radiiSchema.optional(),
	shadows: shadowsSchema.optional(),
	spacing: spacingSchema.optional(),
	motion: motionSchema.optional(),
	gradient: gradientSchema.optional(),
	opacity: opacitySchema.optional()
});
const brandSchema = z.object({
	id: z.string().regex(/^[a-z0-9-]+$/),
	name: z.string().min(1),
	theme: themeSchema,
	shell: z.enum(["sidebar", "topnav"]).default("sidebar"),
	logo: brandLogo.optional()
});
// src/contrast.ts
const AA_CONTRAST_MIN = 4.5;
const parseChannels = (channels) => {
	const parts = channels.trim().split(/\s+/).map(Number);
	if (parts.length !== 3 || !parts.every((n) => Number.isFinite(n) && n >= 0 && n <= 255)) return [
		NaN,
		NaN,
		NaN
	];
	const [r = NaN, g = NaN, b = NaN] = parts;
	return [
		r,
		g,
		b
	];
};
const channelLuminance = (c) => {
	const s = c / 255;
	return s <= .03928 ? s / 12.92 : ((s + .055) / 1.055) ** 2.4;
};
const relativeLuminance = (channels) => {
	const [r, g, b] = parseChannels(channels);
	return .2126 * channelLuminance(r) + .7152 * channelLuminance(g) + .0722 * channelLuminance(b);
};
/** WCAG contrast ratio (1–21) between two `"R G B"` channel-string colours. */
const contrastRatio = (fg, bg) => {
	const l1 = relativeLuminance(fg);
	const l2 = relativeLuminance(bg);
	const [lighter, darker] = l1 >= l2 ? [l1, l2] : [l2, l1];
	return (lighter + .05) / (darker + .05);
};
const PAIRINGS = [
	{
		pairing: "primary/primaryForeground",
		label: "primaryForeground",
		fill: "primary"
	},
	{
		pairing: "destructive/destructiveForeground",
		label: "destructiveForeground",
		fill: "destructive"
	},
	{
		pairing: "secondary/secondaryForeground",
		label: "secondaryForeground",
		fill: "secondary"
	},
	{
		pairing: "accent/accentForeground",
		label: "accentForeground",
		fill: "accent"
	},
	{
		pairing: "primary/background",
		label: "primary",
		fill: "background"
	}
];
const round2 = (n) => Math.round(n * 100) / 100;
const paletteWarnings = (colors, palette) => {
	if (colors === void 0) return [];
	const warnings = [];
	for (const { pairing, label, fill } of PAIRINGS) {
		const foreground = colors[label];
		const background = colors[fill];
		if (typeof foreground !== "string" || typeof background !== "string") continue;
		const raw = contrastRatio(foreground, background);
		if (!Number.isFinite(raw) || raw >= 4.5) continue;
		const ratio = round2(raw);
		warnings.push({
			pairing,
			palette,
			foreground,
			background,
			ratio,
			min: AA_CONTRAST_MIN,
			message: `interactive pairing '${pairing}' (${palette}) is ${ratio}:1 — below the WCAG-AA minimum of ${AA_CONTRAST_MIN}:1`
		});
	}
	return warnings;
};
/**
* Interactive foreground↔background pairings that fall below WCAG-AA, across a
* brand's light (`colors`) and dark (`colorsDark`) palettes. Pure and non-fatal:
* a pairing with either token unset is skipped. Returns `[]` for an AA-safe brand.
*/
const checkInteractiveContrast = (brand) => [...paletteWarnings(brand.theme.colors, "light"), ...paletteWarnings(brand.theme.colorsDark, "dark")];
// src/logos.ts
const KEBOOLA_MARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 300 512\" fill=\"currentColor\"><path d=\"M152 320C79.7 320 24.5 233.9 24.5 124.7C24.5 15.6 70.5 0 152 0s123.6 15.6 123.6 124.7C275.5 233.9 217.7 320 152 320zm-8.7-50.5c0-11.2-9.5-20.3-21.2-20.3s-21.2 9.1-21.2 20.3c0 11.2 9.5 20.3 21.2 20.3s21.2-9.1 21.2-20.3zm55.7 0c0-11.2-9.5-20.3-21.2-20.3s-21.2 9.1-21.2 20.3c0 11.2 9.5 20.3 21.2 20.3s21.2-9.1 21.2-20.3zm68.9 115.1c10.2 9.3 20.2 20.5 29 35c5.9 9.7 2.6 22.1-7.5 27.8c-3.4 1.9-7.1 2.8-10.7 2.8c-7.2 0-14.3-3.6-18.3-10c-6.8-11.1-13.8-18.9-21.7-26.1c-7.3-6.7-15.6-12.7-24.9-19.5c-.7-.5-1.4-1-2.1-1.5c-5-3.6-10-6.8-14.9-9.4c.6 1 1.2 2.1 1.8 3.2c14.4 26.4 29.2 63.1 29.2 104.9c0 11.2-9.5 20.3-21.2 20.3c-11.7 0-21.2-9.1-21.2-20.3c.1-31.8-11.6-62.3-23.8-84.9c-4-7.4-8-13.9-11.6-19.3c-3.4 5.2-7.2 11.3-11 18.3c-12.5 22.7-24.5 53.8-24.4 86c0 11.2-9.5 20.3-21.2 20.3s-21.2-9.1-21.2-20.3c.1-41.8 14.9-78.4 29.2-104.9c.6-1.1 1.2-2.1 1.8-3.2c-5 2.7-9.9 5.8-14.9 9.4c-.6 .5-1.3 .9-1.9 1.4c-9.5 6.9-17.8 12.9-25.1 19.7c-7.9 7.2-14.9 15.1-21.7 26.1c-5.9 9.7-18.9 12.9-29 7.2C.5 441.8-2.9 429.3 3.1 419.7c8.9-14.4 18.8-25.7 29-35c9.2-8.4 18.5-15.1 27.6-21.7c1-.7 2-1.4 3-2.2c4.7-3.5 9.6-6.7 14.6-9.6c-5-2.9-9.8-6.1-14.6-9.6C39.8 325 19.9 302.5 3.1 275.1c-5.9-9.7-2.6-22.1 7.5-27.8c10.1-5.7 23.1-2.5 29 7.2c14.6 23.7 31.1 42 48.7 54.9c18.9 13.7 37.4 20.5 56.5 21.5c1.7-.1 3.5-.1 5.2-.1c1.8 0 3.5 .1 5.2 .1c0 0 0 0 0 0c19.1-1.1 37.6-7.9 56.5-21.5c17.6-12.8 34.2-31.2 48.7-54.9c6-9.7 18.9-12.9 29-7.2c10.1 5.7 13.4 18.1 7.5 27.8c-16.8 27.4-36.7 49.9-59.6 66.6c-4.7 3.5-9.6 6.7-14.5 9.6c4.9 2.9 9.8 6.1 14.5 9.6c.9 .7 1.8 1.3 2.7 2c9.2 6.7 18.5 13.5 27.9 22z\"/></svg>";
const KEBOOLA_WORDMARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 132 33\" fill=\"currentColor\"><path d=\"M130.384 13.2579C131.49 14.3339 132 15.9206 132 17.8738V25.2386C132 26.1727 131.263 26.8812 130.327 26.8812C129.333 26.8812 128.653 26.2007 128.653 25.4364V24.8698C127.632 26.0878 126.071 27.051 123.773 27.051C120.965 27.051 118.468 25.4364 118.468 22.4339V22.3781C118.468 19.1488 120.993 17.5632 124.653 17.5632C126.327 17.5632 127.518 17.818 128.681 18.1856V17.818C128.681 15.6938 127.376 14.5608 124.965 14.5608C123.66 14.5608 122.583 14.7876 121.645 15.1552C121.447 15.2122 121.278 15.2401 121.107 15.2401C120.313 15.2401 119.66 14.6178 119.66 13.8244C119.66 13.2009 120.086 12.6634 120.596 12.4656C122.014 11.9271 123.461 11.5874 125.419 11.5874C127.66 11.5874 129.333 12.1818 130.384 13.2579ZM128.738 21.3579V20.3388C127.858 19.9991 126.695 19.7432 125.334 19.7432C123.121 19.7432 121.816 20.6785 121.816 22.2361V22.292C121.816 23.7368 123.093 24.5581 124.738 24.5581C127.007 24.5581 128.738 23.2552 128.738 21.3579ZM114.951 5.92331C115.916 5.92331 116.682 6.6876 116.682 7.62175V25.1816C116.682 26.1448 115.916 26.8812 114.951 26.8812C114.015 26.8812 113.249 26.1448 113.249 25.1816V7.62175C113.249 6.6876 113.987 5.92331 114.951 5.92331ZM103.293 11.4734C107.888 11.4734 111.264 14.9574 111.264 19.2337V19.2907C111.264 23.539 107.86 27.0789 103.236 27.0789C98.6691 27.0789 95.2927 23.5949 95.2927 19.3477V19.2907C95.2927 15.0144 98.6974 11.4734 103.293 11.4734ZM107.832 19.3477V19.2907C107.832 16.6559 105.931 14.4758 103.236 14.4758C100.485 14.4758 98.7257 16.6291 98.7257 19.2337V19.2907C98.7257 21.8965 100.626 24.0776 103.293 24.0776C106.073 24.0776 107.832 21.9244 107.832 19.3477ZM86.3575 11.4734C90.953 11.4734 94.3283 14.9574 94.3283 19.2337V19.2907C94.3283 23.539 90.9247 27.0789 86.3009 27.0789C81.7337 27.0789 78.3573 23.5949 78.3573 19.3477V19.2907C78.3573 15.0144 81.762 11.4734 86.3575 11.4734ZM90.8964 19.3477V19.2907C90.8964 16.6559 88.9959 14.4758 86.3009 14.4758C83.5493 14.4758 81.7903 16.6291 81.7903 19.2337V19.2907C81.7903 21.8965 83.6908 24.0776 86.3575 24.0776C89.1374 24.0776 90.8964 21.9244 90.8964 19.3477ZM70.3865 11.4734C73.9044 11.4734 77.3374 14.249 77.3374 19.2337V19.2907C77.3374 24.2463 73.9327 27.051 70.3865 27.051C67.8906 27.051 66.3309 25.804 65.2805 24.4173V25.1816C65.2805 26.1157 64.5153 26.8812 63.5498 26.8812C62.6138 26.8812 61.8486 26.1157 61.8486 25.1816V7.62175C61.8486 6.65855 62.5855 5.92331 63.5498 5.92331C64.5153 5.92331 65.2805 6.65855 65.2805 7.62175V14.2781C66.3875 12.7204 67.9472 11.4734 70.3865 11.4734ZM73.8478 19.2907V19.2337C73.8478 16.3452 71.8908 14.4468 69.5647 14.4468C67.2387 14.4468 65.1956 16.3732 65.1956 19.2337V19.2907C65.1956 22.1512 67.2387 24.0776 69.5647 24.0776C71.9191 24.0776 73.8478 22.2361 73.8478 19.2907ZM52.9134 11.4734C57.6787 11.4734 60.033 15.382 60.033 18.837C60.033 19.8002 59.2962 20.4796 58.4167 20.4796H49.0265C49.3955 22.9155 51.1262 24.2754 53.339 24.2754C54.7856 24.2754 55.9197 23.7659 56.8841 22.9725C57.1399 22.7747 57.3663 22.6608 57.7636 22.6608C58.5299 22.6608 59.1253 23.2552 59.1253 24.0486C59.1253 24.4732 58.9272 24.8419 58.6714 25.0967C57.3097 26.3146 55.6085 27.0789 53.2813 27.0789C48.9416 27.0789 45.5946 23.9357 45.5946 19.3186V19.2628C45.5946 14.9854 48.6292 11.4734 52.9134 11.4734ZM48.9982 18.2146H56.686C56.4585 16.0044 55.1546 14.2781 52.8851 14.2781C50.7855 14.2781 49.3106 15.8916 48.9982 18.2146ZM38.3041 15.6368L44.9992 23.9357C45.2822 24.3033 45.4814 24.643 45.4814 25.1816C45.4814 26.1448 44.7151 26.8812 43.7224 26.8812C43.041 26.8812 42.6154 26.5415 42.2476 26.0599L35.8648 17.9308L32.631 21.0461V25.1525C32.631 26.1157 31.8647 26.8812 30.9003 26.8812C29.9077 26.8812 29.1414 26.1157 29.1414 25.1525V8.50002C29.1414 7.53682 29.9077 6.77253 30.9003 6.77253C31.8647 6.77253 32.631 7.53682 32.631 8.50002V16.8838L42.0484 7.42397C42.4457 6.99824 42.8713 6.77253 43.4949 6.77253C44.4593 6.77253 45.1124 7.53682 45.1124 8.38716C45.1124 8.92463 44.8849 9.3213 44.4887 9.69004L38.3041 15.6368ZM9.6977 20.6282C5.08183 20.6282 1.55259 15.0736 1.55259 8.03853C1.55259 1.00454 4.49325 0 9.6977 0C14.9021 0 17.5881 1.00454 17.5881 8.03853C17.5881 15.0736 13.8948 20.6282 9.6977 20.6282ZM18.9532 27.0488C19.3324 27.6711 19.1173 28.4746 18.4744 28.8411C18.2582 28.964 18.0216 29.0221 17.7885 29.0221C17.3255 29.0221 16.8739 28.7919 16.6215 28.3773C16.1857 27.6644 15.7397 27.1571 15.2372 26.6934C14.7346 26.2319 14.1642 25.8185 13.511 25.3369C13.1919 25.1034 12.8738 24.9011 12.5569 24.729C12.5942 24.7961 12.6316 24.8643 12.6701 24.9346C13.5869 26.6376 14.5331 29.0008 14.5377 31.6926C14.5377 32.4145 13.9321 33 13.1851 33C12.4369 33 11.8313 32.4145 11.8313 31.6926C11.8358 29.6456 11.0933 27.6767 10.3101 26.2174C10.0565 25.7414 9.8007 25.3213 9.56979 24.9704C9.35134 25.3045 9.10911 25.7012 8.86689 26.1481C8.07117 27.6142 7.30374 29.6143 7.30827 31.6926C7.30827 32.4145 6.70271 33 5.95566 33C5.2086 33 4.60304 32.4145 4.60304 31.6926C4.60757 29.0008 5.5527 26.6376 6.47067 24.9346C6.50802 24.8643 6.54537 24.7961 6.58386 24.729C6.26579 24.9011 5.94886 25.1034 5.62967 25.3369C4.97543 25.8185 4.40496 26.2319 3.90353 26.6934C3.40097 27.1571 2.95387 27.6644 2.51809 28.3785C2.13891 29.0008 1.30923 29.2076 0.666313 28.8411C0.0222655 28.4746 -0.191662 27.6711 0.187522 27.0488C0.753469 26.118 1.38959 25.3917 2.04156 24.795C2.69467 24.1972 3.3523 23.7256 3.99069 23.2541C4.2929 23.0306 4.60304 22.825 4.91997 22.6384C4.60304 22.4518 4.2929 22.2451 3.99069 22.0216C2.53054 20.9456 1.26169 19.4941 0.187522 17.7319C-0.191662 17.1084 0.0222655 16.3061 0.666313 15.9396C1.30923 15.5731 2.13891 15.7798 2.51809 16.4022C3.44851 17.9297 4.50457 19.1119 5.62854 19.9388C6.834 20.8204 8.01797 21.2573 9.23588 21.3266C9.34681 21.3232 9.45887 21.3199 9.56979 21.3199C9.68185 21.3199 9.79278 21.3232 9.9037 21.3266C11.1216 21.2573 12.3067 20.8204 13.511 19.9388C14.635 19.1119 15.6922 17.9297 16.6215 16.4022C17.0018 15.7798 17.8304 15.5731 18.4744 15.9396C19.1173 16.3061 19.3324 17.1084 18.9532 17.7308C17.879 19.4941 16.609 20.9456 15.1489 22.0216C14.8467 22.2451 14.5365 22.4518 14.2207 22.6384C14.5365 22.825 14.8467 23.0306 15.1489 23.2541C15.7884 23.7256 16.4449 24.1972 17.0992 24.795C17.7511 25.3917 18.3861 26.118 18.9532 27.0488ZM7.79046 16.0659C7.04341 16.0659 6.43784 16.6525 6.43784 17.3744C6.43784 18.0984 7.04341 18.6839 7.79046 18.6839C8.53751 18.6839 9.14307 18.0984 9.14307 17.3744C9.14307 16.6525 8.53751 16.0659 7.79046 16.0659ZM11.3491 16.0659C10.6021 16.0659 9.99652 16.6525 9.99652 17.3744C9.99652 18.0984 10.6021 18.6839 11.3491 18.6839C12.0962 18.6839 12.7017 18.0984 12.7017 17.3744C12.7017 16.6525 12.0962 16.0659 11.3491 16.0659Z\"/></svg>";
const EXAMPLE_CUSTOMER_MARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"currentColor\"><rect width=\"24\" height=\"24\" rx=\"6\"/><path d=\"M12 6l4.5 12h-2.4l-.9-2.6H10.8L9.9 18H7.5L12 6zm0 4.1l-.9 3h1.8l-.9-3z\" fill=\"#fff\"/></svg>";
const EXAMPLE_CUSTOMER_WORDMARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 132 24\" fill=\"currentColor\"><text x=\"0\" y=\"18\" font-family=\"Inter, sans-serif\" font-size=\"20\" font-weight=\"700\">Acme Co.</text></svg>";
const ONCALL_MARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><rect x=\"2\" y=\"4\" width=\"20\" height=\"16\" rx=\"4\"/><path d=\"M5 12h3l2-4 3 8 2-4h4\"/></svg>";
const ONCALL_WORDMARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 116 24\" fill=\"currentColor\"><text x=\"0\" y=\"18\" font-family=\"'Space Grotesk', Inter, sans-serif\" font-size=\"20\" font-weight=\"700\">OnCall</text></svg>";
const GARGOOLA_MARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"382.98 38.69 1316.85 1316.85\"><path fill=\"#e13e36\" fill-rule=\"nonzero\" d=\"M 502.312 104.529 C 511.821 110.468 526.984 123.114 536.189 130.4 L 592.244 174.676 L 714.601 270.825 C 739.519 290.569 793.977 336.08 821.797 348.394 C 882.407 375.223 962.135 385.879 1027.65 396.827 C 1041.86 399.201 1094.48 390.231 1111.73 387.646 C 1159.46 380.498 1224.04 366.825 1266.66 345.072 C 1293.4 331.425 1337.36 293.044 1362.41 273.527 C 1433.22 218.347 1502.37 159.387 1574.31 105.641 C 1567.08 124.74 1560.39 145.909 1554.31 165.42 C 1512.45 299.702 1456.84 488.253 1581.8 595.427 C 1514.03 640.125 1503.32 711.649 1553.96 775.183 C 1566.76 791.247 1578.84 801.944 1594.98 814.784 L 1484.57 909.661 L 1198.61 1155.48 L 1098.25 1241.37 C 1080.51 1256.45 1058.29 1274.05 1041.6 1289.69 C 1020.32 1273.18 993.829 1249.36 973.033 1231.48 L 861.869 1135.86 L 635.376 940.646 C 587.212 899.248 536.526 854.811 487.816 814.625 C 532.058 782.466 567.115 727.993 556.718 671.502 C 550.317 636.722 526.021 614.625 498.514 595.437 C 512.172 583.114 530.159 566.231 539.107 550.208 C 606.512 429.506 558.96 279.996 519.302 157.861 C 513.573 140.217 506.848 122.495 502.312 104.529 Z M 958.57 809.388 C 945.625 695.08 870.821 653.184 765.045 630.345 C 750.512 627.206 735.893 626.057 721.288 623.776 C 732.315 676.831 750.31 738.869 798.596 769.825 C 842.521 797.985 907.509 806.647 958.57 809.388 Z M 1128.63 809.437 C 1192.14 805.338 1267.41 796.969 1311.86 745.348 C 1340.28 712.354 1352.35 668.193 1359.96 625.999 C 1359.82 625.49 1359.69 624.982 1359.55 624.474 C 1357.61 624.11 1358.29 623.967 1356.56 624.475 C 1292.98 630.948 1205.85 656.063 1163.29 708.176 C 1140.61 735.959 1129.68 772.522 1125.33 808.032 C 1125.47 808.571 1125.62 809.11 1125.77 809.648 C 1127.23 809.688 1127.34 809.876 1128.63 809.437 Z\"/></svg>";
const GARGOOLA_WORDMARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 732 161\"><path fill=\"#e13e36\" fill-rule=\"evenodd\" d=\"m235.65 49.5q5.13 0 9.39 1.74 4.38 1.56 7.56 4.2 2.45 1.94 4.05 4.03v-3.4q0-2.58 1.59-4.23 1.8-1.86 4.41-1.86 2.61 0 4.23 1.74 1.77 1.68 1.77 4.35v52.95q0 10.14-4.41 16.62-4.23 6.33-11.4 9.36-7.11 3-15.6 3-3 0-7.2-0.72-3.99-0.81-7.44-1.92-3.39-1.11-4.74-2.19-2.7-1.41-3.66-3.21-0.84-2.01 0.03-3.99 1.11-2.61 3.09-3.21 2.13-0.63 4.77 0.36 1.17 0.36 3.63 1.32 2.61 0.96 5.64 1.8 3.24 0.72 5.94 0.72 9.6 0 14.46-4.44 4.89-4.47 4.89-12.06v-6.26q-0.41 0.69-0.93 1.4-1.86 2.49-4.92 4.68-2.94 2.16-6.84 3.6-3.81 1.32-7.95 1.32-8.46 0-15.33-4.2-6.66-4.26-10.68-11.64-3.84-7.44-3.84-16.98 0-9.63 3.84-16.98 4.02-7.47 10.68-11.64 6.69-4.26 14.97-4.26zm1.77 11.04q-5.79 0-10.26 2.94-4.44 2.76-6.96 7.8-2.49 4.83-2.49 11.1 0 6.15 2.49 11.1 2.52 4.95 6.96 7.92 4.47 2.76 10.26 2.76 5.88 0 10.26-2.76 4.5-2.91 6.96-7.8 2.58-4.98 2.58-11.22 0-6.33-2.58-11.22-2.46-4.98-6.96-7.8-4.38-2.82-10.26-2.82zm98.73-10.56q2.61 0 4.29 1.74 1.71 1.68 1.71 4.35v51.84q0 2.58-1.71 4.41-1.71 1.68-4.29 1.68-2.61 0-4.35-1.68-1.65-1.83-1.65-4.41v-2.43q-1.6 2.08-4.11 4.08-2.91 2.4-7.08 4.08-3.96 1.56-8.7 1.56-8.22 0-14.94-4.2-6.51-4.29-10.44-11.64-3.72-7.5-3.72-17.04 0-9.66 3.84-17.04 3.84-7.38 10.32-11.52 6.57-4.26 14.58-4.26 5.16 0 9.54 1.74 4.38 1.53 7.56 4.2 1.81 1.43 3.15 2.99v-2.36q0-2.58 1.65-4.23 1.74-1.86 4.35-1.86zm-24.48 54.18q5.64 0 9.93-2.76 4.29-2.97 6.6-7.92 2.52-4.98 2.52-11.16 0-6.18-2.52-11.04-2.31-5.01-6.6-7.8-4.29-2.94-9.93-2.94-5.52 0-9.87 2.94-4.23 2.73-6.72 7.68-2.37 4.89-2.37 11.16 0 6.18 2.37 11.16 2.46 4.95 6.72 7.92 4.32 2.76 9.87 2.76zm53.67 9.84q-2.61 0-4.38-1.68-1.62-1.83-1.62-4.41v-50.94q0-2.58 1.62-4.29 1.8-1.8 4.38-1.8 2.61 0 4.26 1.8 1.74 1.71 1.74 4.29v5.25q0.75-1.61 1.74-3.06 2.07-2.97 4.8-5.04 2.79-2.25 6.12-3.36 3.42-1.26 7.14-1.26 3.12 0 5.34 1.74 2.43 1.56 2.43 4.08 0 3.24-1.71 4.8-1.65 1.41-3.69 1.41-1.89 0-3.63-0.69-1.56-0.84-3.84-0.84-2.58 0-5.28 1.44-2.49 1.23-4.68 3.84-2.13 2.52-3.48 6.12-1.26 3.39-1.26 7.68v28.83q0 2.58-1.74 4.41-1.68 1.68-4.26 1.68zm68.31-64.5q5.13 0 9.39 1.74 4.38 1.56 7.56 4.2 2.45 1.94 4.05 4.03v-3.4q0-2.58 1.59-4.23 1.8-1.86 4.41-1.86 2.61 0 4.23 1.74 1.77 1.68 1.77 4.35v52.95q0 10.14-4.41 16.62-4.23 6.33-11.4 9.36-7.11 3-15.6 3-3 0-7.2-0.72-3.99-0.81-7.44-1.92-3.39-1.11-4.74-2.19-2.7-1.41-3.66-3.21-0.84-2.01 0.03-3.99 1.11-2.61 3.09-3.21 2.13-0.63 4.77 0.36 1.17 0.36 3.63 1.32 2.61 0.96 5.64 1.8 3.24 0.72 5.94 0.72 9.6 0 14.46-4.44 4.89-4.47 4.89-12.06v-6.26q-0.41 0.69-0.93 1.4-1.86 2.49-4.92 4.68-2.94 2.16-6.84 3.6-3.81 1.32-7.95 1.32-8.46 0-15.33-4.2-6.66-4.26-10.68-11.64-3.84-7.44-3.84-16.98 0-9.63 3.84-16.98 4.02-7.47 10.68-11.64 6.69-4.26 14.97-4.26zm1.77 11.04q-5.79 0-10.26 2.94-4.44 2.76-6.96 7.8-2.49 4.83-2.49 11.1 0 6.15 2.49 11.1 2.52 4.95 6.96 7.92 4.47 2.76 10.26 2.76 5.88 0 10.26-2.76 4.5-2.91 6.96-7.8 2.58-4.98 2.58-11.22 0-6.33-2.58-11.22-2.46-4.98-6.96-7.8-4.38-2.82-10.26-2.82zm106.49 21.87q0 9.6-4.35 17.07-4.14 7.35-11.4 11.64-7.14 4.08-16.11 4.08-8.94 0-16.17-4.08-7.2-4.29-11.52-11.64-4.2-7.47-4.2-17.07 0-9.63 4.2-17.01 4.32-7.44 11.52-11.64 7.23-4.26 16.17-4.26 8.97 0 16.11 4.26 7.26 4.2 11.4 11.64 4.35 7.38 4.35 17.01zm-12 0q0-6.54-2.67-11.37-2.61-4.95-7.2-7.68-4.41-2.82-9.99-2.82-5.55 0-10.17 2.82-4.41 2.73-7.08 7.68-2.64 4.83-2.64 11.37 0 6.42 2.64 11.31 2.67 4.89 7.08 7.68 4.62 2.76 10.17 2.76 5.58 0 9.99-2.76 4.59-2.79 7.2-7.68 2.67-4.89 2.67-11.31zm86 0q0 9.6-4.35 17.07-4.14 7.35-11.4 11.64-7.14 4.08-16.11 4.08-8.94 0-16.17-4.08-7.2-4.29-11.52-11.64-4.2-7.47-4.2-17.07 0-9.63 4.2-17.01 4.32-7.44 11.52-11.64 7.23-4.26 16.17-4.26 8.97 0 16.11 4.26 7.26 4.2 11.4 11.64 4.35 7.38 4.35 17.01zm-12 0q0-6.54-2.67-11.37-2.61-4.95-7.2-7.68-4.41-2.82-9.99-2.82-5.55 0-10.17 2.82-4.41 2.73-7.08 7.68-2.64 4.83-2.64 11.37 0 6.42 2.64 11.31 2.67 4.89 7.08 7.68 4.62 2.76 10.17 2.76 5.58 0 9.99-2.76 4.59-2.79 7.2-7.68 2.67-4.89 2.67-11.31zm38.48-51.12v76.62q0 2.58-1.83 4.41-1.68 1.68-4.26 1.68-2.52 0-4.26-1.68-1.65-1.83-1.65-4.41v-76.62q0-2.58 1.65-4.29 1.86-1.8 4.44-1.8 2.61 0 4.2 1.8 1.71 1.71 1.71 4.29zm69.76 18.69q2.61 0 4.29 1.74 1.71 1.68 1.71 4.35v51.84q0 2.58-1.71 4.41-1.71 1.68-4.29 1.68-2.61 0-4.35-1.68-1.65-1.83-1.65-4.41v-2.43q-1.6 2.08-4.11 4.08-2.91 2.4-7.08 4.08-3.96 1.56-8.7 1.56-8.22 0-14.94-4.2-6.51-4.29-10.44-11.64-3.72-7.5-3.72-17.04 0-9.66 3.84-17.04 3.84-7.38 10.32-11.52 6.57-4.26 14.58-4.26 5.16 0 9.54 1.74 4.38 1.53 7.56 4.2 1.81 1.43 3.15 2.99v-2.36q0-2.58 1.65-4.23 1.74-1.86 4.35-1.86zm-24.48 54.18q5.64 0 9.93-2.76 4.29-2.97 6.6-7.92 2.52-4.98 2.52-11.16 0-6.18-2.52-11.04-2.31-5.01-6.6-7.8-4.29-2.94-9.93-2.94-5.52 0-9.87 2.94-4.23 2.73-6.72 7.68-2.37 4.89-2.37 11.16 0 6.18 2.37 11.16 2.46 4.95 6.72 7.92 4.32 2.76 9.87 2.76z\"/><g transform=\"matrix(0.119928, 0, 0, 0.119928, 93.905678, 81.484619)\"><g><g transform=\"matrix(1 0 0 1.00024 0 0)\"><path fill=\"#e13e36\" fill-rule=\"nonzero\" transform=\" translate(-1041.398, -697.1095)\" d=\"M 502.312 104.529 C 511.821 110.468 526.984 123.114 536.189 130.4 L 592.244 174.676 L 714.601 270.825 C 739.519 290.569 793.977 336.08 821.797 348.394 C 882.407 375.223 962.135 385.879 1027.65 396.827 C 1041.86 399.201 1094.48 390.231 1111.73 387.646 C 1159.46 380.498 1224.04 366.825 1266.66 345.072 C 1293.4 331.425 1337.36 293.044 1362.41 273.527 C 1433.22 218.347 1502.37 159.387 1574.31 105.641 C 1567.08 124.74 1560.39 145.909 1554.31 165.42 C 1512.45 299.702 1456.84 488.253 1581.8 595.427 C 1514.03 640.125 1503.32 711.649 1553.96 775.183 C 1566.76 791.247 1578.84 801.944 1594.98 814.784 L 1484.57 909.661 L 1198.61 1155.48 L 1098.25 1241.37 C 1080.51 1256.45 1058.29 1274.05 1041.6 1289.69 C 1020.32 1273.18 993.829 1249.36 973.033 1231.48 L 861.869 1135.86 L 635.376 940.646 C 587.212 899.248 536.526 854.811 487.816 814.625 C 532.058 782.466 567.115 727.993 556.718 671.502 C 550.317 636.722 526.021 614.625 498.514 595.437 C 512.172 583.114 530.159 566.231 539.107 550.208 C 606.512 429.506 558.96 279.996 519.302 157.861 C 513.573 140.217 506.848 122.495 502.312 104.529 Z M 958.57 809.388 C 945.625 695.08 870.821 653.184 765.045 630.345 C 750.512 627.206 735.893 626.057 721.288 623.776 C 732.315 676.831 750.31 738.869 798.596 769.825 C 842.521 797.985 907.509 806.647 958.57 809.388 Z M 1128.63 809.437 C 1192.14 805.338 1267.41 796.969 1311.86 745.348 C 1340.28 712.354 1352.35 668.193 1359.96 625.999 C 1359.82 625.49 1359.69 624.982 1359.55 624.474 C 1357.61 624.11 1358.29 623.967 1356.56 624.475 C 1292.98 630.948 1205.85 656.063 1163.29 708.176 C 1140.61 735.959 1129.68 772.522 1125.33 808.032 C 1125.47 808.571 1125.62 809.11 1125.77 809.648 C 1127.23 809.688 1127.34 809.876 1128.63 809.437 Z\" stroke-linecap=\"round\"/></g></g></g></svg>";
const AGNES_MARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"currentColor\"><rect width=\"24\" height=\"24\" rx=\"7\"/><path d=\"M12 5.5l5.2 13h-2.7l-1-2.8h-3l-1 2.8H6.8L12 5.5zm0 4.6l-1.1 3.4h2.2L12 10.1z\" fill=\"#fff\"/></svg>";
const AGNES_WORDMARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 132 24\" fill=\"currentColor\"><text x=\"0\" y=\"18\" font-family=\"Inter, sans-serif\" font-size=\"20\" font-weight=\"700\">Agnes</text></svg>";
const HARBOR_MARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"currentColor\"><rect width=\"24\" height=\"24\" rx=\"5\"/><path d=\"M5 8.5h14M12 8.5v8m0 0c-2.8 0-5-2-5-4.4m5 4.4c2.8 0 5-2 5-4.4\" fill=\"none\" stroke=\"#fff\" stroke-width=\"1.8\" stroke-linecap=\"round\"/></svg>";
const HARBOR_WORDMARK_SVG = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 132 24\" fill=\"currentColor\"><text x=\"0\" y=\"18\" font-family=\"Manrope, sans-serif\" font-size=\"20\" font-weight=\"700\" letter-spacing=\"0.5\">Harbor</text></svg>";
// src/brands/agnes.ts
const agnes = {
	id: "agnes",
	name: "Agnes",
	shell: "sidebar",
	logo: {
		mark: AGNES_MARK_SVG,
		wordmark: AGNES_WORDMARK_SVG
	},
	theme: {
		colors: {
			primary: "2 132 199",
			primaryForeground: "255 255 255",
			background: "243 246 250",
			foreground: "15 23 42",
			card: "255 255 255",
			cardForeground: "15 23 42",
			popover: "255 255 255",
			popoverForeground: "15 23 42",
			muted: "245 247 251",
			mutedForeground: "71 85 105",
			secondary: "234 238 242",
			secondaryForeground: "15 23 42",
			accent: "224 242 254",
			accentForeground: "3 105 161",
			border: "226 232 240",
			input: "226 232 240",
			ring: "2 132 199",
			ringOffset: "243 246 250",
			destructive: "220 38 38",
			destructiveForeground: "255 255 255",
			success: "15 118 110",
			warning: "161 98 7",
			info: "28 57 148",
			chart: {
				chart1: "2 132 199",
				chart2: "125 211 252",
				chart3: "15 118 110",
				chart4: "161 98 7",
				chart5: "71 85 105"
			},
			sidebar: {
				sidebar: "255 255 255",
				sidebarForeground: "15 23 42",
				sidebarPrimary: "2 132 199",
				sidebarPrimaryForeground: "255 255 255",
				sidebarAccent: "224 242 254",
				sidebarAccentForeground: "3 105 161",
				sidebarBorder: "226 232 240",
				sidebarRing: "2 132 199"
			}
		},
		colorsDark: {
			primary: "78 192 150",
			primaryForeground: "14 21 37",
			background: "12 18 36",
			foreground: "243 246 255",
			card: "20 26 48",
			cardForeground: "243 246 255",
			popover: "20 26 48",
			popoverForeground: "243 246 255",
			muted: "28 36 64",
			mutedForeground: "185 193 216",
			secondary: "28 36 64",
			secondaryForeground: "243 246 255",
			accent: "29 53 64",
			accentForeground: "118 214 173",
			border: "40 50 85",
			input: "40 50 85",
			ring: "78 192 150",
			ringOffset: "12 18 36",
			destructive: "255 155 155",
			destructiveForeground: "12 18 36",
			success: "134 225 163",
			warning: "255 217 112",
			info: "168 193 255",
			chart: {
				chart1: "78 192 150",
				chart2: "118 214 173",
				chart3: "168 193 255",
				chart4: "255 217 112",
				chart5: "185 193 216"
			},
			sidebar: {
				sidebar: "20 26 48",
				sidebarForeground: "243 246 255",
				sidebarPrimary: "78 192 150",
				sidebarPrimaryForeground: "14 21 37",
				sidebarAccent: "29 53 64",
				sidebarAccentForeground: "118 214 173",
				sidebarBorder: "40 50 85",
				sidebarRing: "78 192 150"
			}
		},
		typography: {
			fontFamily: "'Inter', system-ui, sans-serif",
			fontDisplay: "'Inter', system-ui, sans-serif",
			fontMono: "'JetBrains Mono', ui-monospace, monospace",
			letterSpacing: { chrome: "0.02em" }
		},
		radii: {
			sm: "0.5rem",
			md: "0.75rem",
			lg: "1rem"
		},
		shadows: {
			sm: "0 1px 2px -1px rgb(15 23 42 / 0.08)",
			md: "0 8px 20px -10px rgb(15 23 42 / 0.14)",
			lg: "0 18px 44px -16px rgb(15 23 42 / 0.18)",
			lift: "0 28px 64px -24px rgb(15 23 42 / 0.26)"
		},
		spacing: {
			gutter: "1.5rem",
			inset: "1.25rem"
		},
		motion: {
			duration: "200ms",
			easing: "cubic-bezier(0.32, 0.72, 0, 1)"
		},
		gradient: { brand: "linear-gradient(135deg, rgb(2 132 199), rgb(125 211 252))" }
	}
};
// src/brands/example-customer.ts
const exampleCustomer = {
	id: "example-customer",
	name: "Acme Co.",
	shell: "sidebar",
	logo: {
		mark: EXAMPLE_CUSTOMER_MARK_SVG,
		wordmark: EXAMPLE_CUSTOMER_WORDMARK_SVG
	},
	theme: {
		colors: {
			foreground: "26 21 35",
			background: "250 250 249",
			muted: "244 242 248",
			mutedForeground: "107 95 122",
			border: "221 216 232",
			primary: "124 58 237",
			primaryForeground: "255 255 255",
			destructive: "185 28 28",
			destructiveForeground: "255 255 255"
		},
		typography: {
			fontFamily: "'Inter', sans-serif",
			weight: {
				normal: "400",
				medium: "600",
				semibold: "700",
				bold: "800"
			},
			letterSpacing: { chrome: "0.02em" }
		},
		radii: {
			sm: "0.375rem",
			md: "0.5rem",
			lg: "0.75rem"
		}
	}
};
// src/brands/keboola.ts
const keboola = {
	id: "keboola",
	name: "Keboola (V2)",
	shell: "sidebar",
	logo: {
		mark: KEBOOLA_MARK_SVG,
		wordmark: KEBOOLA_WORDMARK_SVG
	},
	theme: {
		colors: {
			foreground: "30 41 59",
			background: "249 250 251",
			muted: "241 245 249",
			mutedForeground: "100 116 139",
			border: "229 231 235",
			primary: "37 99 235",
			primaryForeground: "255 255 255",
			destructive: "220 38 38",
			destructiveForeground: "255 255 255"
		},
		colorsDark: {
			foreground: "229 230 236",
			background: "19 19 23",
			muted: "38 38 45",
			mutedForeground: "141 144 153",
			border: "52 53 62",
			primary: "31 143 255",
			primaryForeground: "10 26 43",
			destructive: "248 113 113",
			destructiveForeground: "255 255 255"
		},
		typography: {
			weight: {
				normal: "400",
				medium: "500",
				semibold: "600",
				bold: "700"
			},
			letterSpacing: { chrome: "1px" }
		},
		radii: {
			sm: "0.25rem",
			md: "0.375rem",
			lg: "0.5rem"
		}
	}
};
// src/brands/gargoola.ts
const gargoola = {
	id: "gargoola",
	name: "Gargoola",
	shell: "sidebar",
	logo: {
		mark: GARGOOLA_MARK_SVG,
		wordmark: GARGOOLA_WORDMARK_SVG
	},
	theme: {
		...keboola.theme,
		colors: {
			...keboola.theme.colors,
			primary: "200 55 50",
			primaryForeground: "255 255 255",
			accent: "250 233 232",
			accentForeground: "140 42 36",
			sidebar: {
				sidebar: "255 255 255",
				sidebarForeground: "30 41 59",
				sidebarPrimary: "200 55 50",
				sidebarPrimaryForeground: "255 255 255",
				sidebarAccent: "250 233 232",
				sidebarAccentForeground: "140 42 36",
				sidebarBorder: "229 231 235",
				sidebarRing: "200 55 50"
			}
		},
		colorsDark: {
			...keboola.theme.colorsDark,
			primary: "248 113 113",
			primaryForeground: "43 10 10",
			destructiveForeground: "43 10 10",
			accent: "42 29 30",
			accentForeground: "243 176 171",
			sidebar: {
				sidebar: "14 14 18",
				sidebarForeground: "229 230 236",
				sidebarPrimary: "248 113 113",
				sidebarPrimaryForeground: "43 10 10",
				sidebarAccent: "38 38 45",
				sidebarAccentForeground: "229 230 236",
				sidebarBorder: "48 49 57",
				sidebarRing: "248 113 113"
			}
		}
	}
};
// src/brands/harbor.ts
const harbor = {
	id: "harbor",
	name: "Harbor",
	shell: "sidebar",
	logo: {
		mark: HARBOR_MARK_SVG,
		wordmark: HARBOR_WORDMARK_SVG
	},
	theme: {
		colors: {
			primary: "26 111 184",
			primaryForeground: "255 255 255",
			background: "242 246 250",
			foreground: "22 43 56",
			card: "255 255 255",
			cardForeground: "22 43 56",
			popover: "255 255 255",
			popoverForeground: "22 43 56",
			muted: "237 242 247",
			mutedForeground: "82 106 123",
			secondary: "220 231 240",
			secondaryForeground: "22 43 56",
			accent: "232 241 249",
			accentForeground: "22 43 56",
			border: "212 226 237",
			input: "212 226 237",
			ring: "26 111 184",
			ringOffset: "242 246 250",
			destructive: "180 52 42",
			destructiveForeground: "255 255 255",
			success: "25 87 87",
			successForeground: "255 255 255",
			warning: "254 183 20",
			warningForeground: "22 43 56",
			info: "26 111 184",
			infoForeground: "255 255 255",
			chart: {
				chart1: "26 111 184",
				chart2: "254 183 20",
				chart3: "31 74 99",
				chart4: "25 87 87",
				chart5: "127 176 214"
			},
			sidebar: {
				sidebar: "22 43 56",
				sidebarForeground: "223 233 241",
				sidebarPrimary: "95 176 234",
				sidebarPrimaryForeground: "15 30 40",
				sidebarAccent: "29 55 69",
				sidebarAccentForeground: "254 183 20",
				sidebarBorder: "40 72 87",
				sidebarRing: "95 176 234"
			}
		},
		colorsDark: {
			primary: "95 176 234",
			primaryForeground: "15 30 40",
			background: "15 30 40",
			foreground: "238 245 250",
			card: "22 43 56",
			cardForeground: "238 245 250",
			popover: "22 43 56",
			popoverForeground: "238 245 250",
			muted: "29 55 69",
			mutedForeground: "168 191 205",
			secondary: "29 55 69",
			secondaryForeground: "238 245 250",
			accent: "27 58 77",
			accentForeground: "159 208 242",
			border: "40 72 87",
			input: "40 72 87",
			ring: "95 176 234",
			ringOffset: "15 30 40",
			destructive: "240 138 125",
			destructiveForeground: "15 30 40",
			success: "95 191 174",
			successForeground: "15 30 40",
			warning: "253 199 90",
			warningForeground: "15 30 40",
			info: "95 176 234",
			infoForeground: "15 30 40",
			chart: {
				chart1: "95 176 234",
				chart2: "253 199 90",
				chart3: "159 208 242",
				chart4: "95 191 174",
				chart5: "143 169 189"
			},
			sidebar: {
				sidebar: "22 43 56",
				sidebarForeground: "238 245 250",
				sidebarPrimary: "95 176 234",
				sidebarPrimaryForeground: "15 30 40",
				sidebarAccent: "27 58 77",
				sidebarAccentForeground: "253 199 90",
				sidebarBorder: "40 72 87",
				sidebarRing: "95 176 234"
			}
		},
		typography: {
			fontFamily: "'Manrope', system-ui, sans-serif",
			fontDisplay: "'Manrope', system-ui, sans-serif",
			fontMono: "'JetBrains Mono', ui-monospace, monospace",
			weight: {
				normal: "400",
				medium: "500",
				semibold: "600",
				bold: "700"
			},
			letterSpacing: { chrome: "0.05em" }
		},
		radii: {
			sm: "0.25rem",
			md: "0.375rem",
			lg: "0.5rem"
		},
		shadows: {
			sm: "0 1px 2px -1px rgb(22 43 56 / 0.12)",
			md: "0 6px 18px -8px rgb(22 43 56 / 0.18)",
			lg: "0 16px 40px -14px rgb(22 43 56 / 0.24)",
			lift: "0 26px 60px -22px rgb(22 43 56 / 0.32)"
		},
		spacing: {
			gutter: "1.25rem",
			inset: "1.25rem"
		},
		motion: {
			duration: "160ms",
			easing: "cubic-bezier(0.4, 0, 0.2, 1)"
		},
		gradient: { brand: "linear-gradient(135deg, rgb(22 43 56), rgb(26 111 184) 60%, rgb(254 183 20))" }
	}
};
// src/brands/kbc-ui.ts
const colors = {
	background: "249 250 251",
	foreground: "30 41 59",
	muted: "241 245 249",
	mutedForeground: "100 116 139",
	border: "229 231 235",
	input: "203 213 225",
	ring: "31 143 255",
	primary: "31 143 255",
	primaryForeground: "255 255 255",
	secondary: "226 232 240",
	secondaryForeground: "30 41 59",
	accent: "224 242 254",
	accentForeground: "13 65 127",
	card: "255 255 255",
	cardForeground: "30 41 59",
	popover: "255 255 255",
	popoverForeground: "30 41 59",
	destructive: "220 38 38",
	destructiveForeground: "255 255 255",
	success: "31 157 97",
	successForeground: "255 255 255",
	warning: "245 147 0",
	warningForeground: "82 49 0",
	info: "31 143 255",
	infoForeground: "255 255 255",
	chart: {
		chart1: "31 143 255",
		chart2: "0 194 135",
		chart3: "146 92 255",
		chart4: "245 147 0",
		chart5: "237 94 223"
	},
	sidebar: {
		sidebar: "255 255 255",
		sidebarForeground: "30 41 59",
		sidebarPrimary: "31 143 255",
		sidebarPrimaryForeground: "255 255 255",
		sidebarAccent: "219 234 254",
		sidebarAccentForeground: "30 41 59",
		sidebarBorder: "229 231 235",
		sidebarRing: "31 143 255"
	}
};
const sidebarColorsDark = {
	sidebar: "14 14 18",
	sidebarForeground: "229 230 236",
	sidebarPrimary: "31 143 255",
	sidebarPrimaryForeground: "10 26 43",
	sidebarAccent: "38 38 45",
	sidebarAccentForeground: "229 230 236",
	sidebarBorder: "48 49 57",
	sidebarRing: "31 143 255"
};
const colorsDark = {
	...keboola.theme.colorsDark,
	card: "34 34 40",
	cardForeground: "229 230 236",
	popover: "38 38 45",
	popoverForeground: "229 230 236",
	secondary: "47 48 55",
	secondaryForeground: "229 230 236",
	accent: "30 58 95",
	accentForeground: "147 197 253",
	input: "114 116 128",
	inputBackground: "47 48 55",
	ring: "31 143 255",
	destructiveForeground: "43 8 8",
	success: "62 207 127",
	successForeground: "4 33 18",
	warning: "255 190 92",
	warningForeground: "26 18 5",
	info: "92 173 255",
	infoForeground: "10 26 43",
	chart: {
		chart1: "92 173 255",
		chart2: "0 230 161",
		chart3: "173 133 255",
		chart4: "255 190 92",
		chart5: "242 140 232"
	},
	sidebar: sidebarColorsDark
};
/**
* The kbc-ui brand.
*
* - Light: Keboola's brand-blue (`31 143 255`) palette with a white sidebar.
* - Dark: the `keboola` dark palette, pinned in full and tuned to AA.
* - Typography and radii: the `keboola` brand's.
*/
const kbcUi = {
	id: "kbc-ui",
	name: "Keboola",
	shell: "sidebar",
	logo: { mark: KEBOOLA_MARK_SVG },
	theme: {
		colors,
		colorsDark,
		typography: keboola.theme.typography,
		radii: keboola.theme.radii
	}
};
// src/brands/keboola-portal.ts
const keboolaPortal = {
	id: "keboola-portal",
	name: "Keboola Portal",
	shell: "sidebar",
	logo: { mark: KEBOOLA_MARK_SVG },
	theme: {
		colors: {
			foreground: "30 41 59",
			background: "249 250 251",
			muted: "241 245 249",
			mutedForeground: "100 116 139",
			border: "229 231 235",
			primary: "79 70 229",
			primaryForeground: "255 255 255",
			secondary: "241 245 249",
			secondaryForeground: "30 41 59",
			accent: "237 233 254",
			accentForeground: "76 29 149",
			destructive: "220 38 38",
			destructiveForeground: "255 255 255",
			ring: "79 70 229",
			chart: {
				chart1: "79 70 229",
				chart2: "168 85 247",
				chart3: "13 148 136",
				chart4: "217 119 6",
				chart5: "225 29 72"
			}
		},
		colorsDark: {
			foreground: "229 230 236",
			background: "19 19 23",
			muted: "38 38 45",
			mutedForeground: "141 144 153",
			border: "52 53 62",
			primary: "129 140 248",
			primaryForeground: "23 23 32",
			secondary: "38 38 45",
			secondaryForeground: "229 230 236",
			accent: "42 31 77",
			accentForeground: "196 181 253",
			destructive: "248 113 113",
			destructiveForeground: "32 12 12",
			ring: "129 140 248",
			chart: {
				chart1: "129 140 248",
				chart2: "192 132 252",
				chart3: "45 212 191",
				chart4: "251 191 36",
				chart5: "251 113 133"
			}
		},
		typography: {
			weight: {
				normal: "400",
				medium: "500",
				semibold: "600",
				bold: "700"
			},
			letterSpacing: { chrome: "1px" }
		},
		radii: {
			sm: "0.25rem",
			md: "0.375rem",
			lg: "0.5rem"
		},
		gradient: { brand: "linear-gradient(135deg, rgb(79 70 229), rgb(139 92 246))" }
	}
};
// src/brands/oncall.ts
const oncall = {
	id: "oncall",
	name: "OnCall",
	shell: "sidebar",
	logo: {
		mark: ONCALL_MARK_SVG,
		wordmark: ONCALL_WORDMARK_SVG
	},
	theme: {
		colors: {
			foreground: "15 23 42",
			background: "248 250 252",
			card: "255 255 255",
			cardForeground: "15 23 42",
			popover: "255 255 255",
			popoverForeground: "15 23 42",
			muted: "241 245 249",
			mutedForeground: "71 85 105",
			border: "226 232 240",
			input: "226 232 240",
			primary: "14 116 144",
			primaryForeground: "255 255 255",
			secondary: "241 245 249",
			secondaryForeground: "15 23 42",
			accent: "13 148 136",
			accentForeground: "255 255 255",
			ring: "14 116 144",
			destructive: "220 38 38",
			destructiveForeground: "255 255 255",
			warning: "217 119 6",
			warningForeground: "255 255 255",
			success: "22 163 74",
			successForeground: "255 255 255",
			info: "2 132 199",
			infoForeground: "255 255 255",
			chart: {
				chart1: "14 116 144",
				chart2: "13 148 136",
				chart3: "217 119 6",
				chart4: "220 38 38",
				chart5: "124 58 237"
			}
		},
		colorsDark: {
			foreground: "226 232 240",
			background: "10 14 22",
			card: "23 31 47",
			cardForeground: "226 232 240",
			popover: "29 39 57",
			popoverForeground: "226 232 240",
			muted: "33 44 63",
			mutedForeground: "148 163 184",
			border: "44 57 78",
			input: "44 57 78",
			primary: "34 211 238",
			primaryForeground: "8 15 20",
			secondary: "30 41 59",
			secondaryForeground: "226 232 240",
			accent: "45 212 191",
			accentForeground: "4 10 12",
			ring: "34 211 238",
			destructive: "248 113 113",
			destructiveForeground: "8 15 20",
			warning: "251 191 36",
			warningForeground: "8 15 20",
			success: "74 222 128",
			successForeground: "8 15 20",
			info: "56 189 248",
			infoForeground: "8 15 20",
			chart: {
				chart1: "34 211 238",
				chart2: "45 212 191",
				chart3: "251 191 36",
				chart4: "248 113 113",
				chart5: "167 139 250"
			},
			sidebar: {
				sidebar: "7 10 16",
				sidebarForeground: "203 213 225",
				sidebarPrimary: "34 211 238",
				sidebarPrimaryForeground: "8 15 20",
				sidebarAccent: "20 27 40",
				sidebarAccentForeground: "226 232 240",
				sidebarBorder: "30 41 59",
				sidebarRing: "34 211 238"
			}
		},
		typography: {
			fontFamily: "'Inter', sans-serif",
			fontDisplay: "'Space Grotesk', 'Inter', sans-serif",
			fontMono: "'JetBrains Mono', ui-monospace, monospace"
		},
		radii: {
			sm: "0.125rem",
			md: "0.25rem",
			lg: "0.375rem"
		},
		gradient: { brand: "linear-gradient(135deg, rgb(34 211 238), rgb(45 212 191))" }
	}
};
// src/brands/index.ts
/**
* Every brand this registry ships, for id-based lookup by consumers (frozen).
*
* The `@__PURE__` annotation is load-bearing: without it a bundler reads the
* top-level `Object.freeze(...)` call as a side effect, cannot drop this
* binding, and therefore keeps EVERY brand (with its inline logo SVGs) in any
* bundle that imports so much as `keboola` — which is what pushed
* `@keboola/design/testing` over its size budget.
*/
const brands = /* @__PURE__ */ Object.freeze([
	keboola,
	keboolaPortal,
	kbcUi,
	exampleCustomer,
	oncall,
	gargoola,
	agnes,
	harbor
]);
export { AA_CONTRAST_MIN, agnes, brandSchema, brands, checkInteractiveContrast, contrastRatio, exampleCustomer, gargoola, harbor, kbcUi, keboola, keboolaPortal, oncall };

//# sourceMappingURL=index.js.map